import { type JSX } from "react"
import { DayObject, DayRange, TimeOfDay, Timestamp } from "@/types/date"
import { FlightTravelers } from "@/types/flights/flight-search-options"
import { UnitSystem } from "@/types/geo"
import { DetailedInspiration } from "@/types/inspiration"
import { ItineraryEventType } from "@/types/itinerary"
import { EditorialRating, Place, PlaceType, Restaurant } from "@/types/place"
import { Budget, TimeFrame, TravelerGroup, TripVibe } from "@/types/trip"
import pluralize from "pluralize"

import { convertISODateToTimestamp, nowMillis } from "@/lib/date-utils"
import type { Translator } from "@/lib/i18n/i18n-context"
import type { TranslationKey } from "@/lib/i18n/types"
import { parseLocale } from "@/lib/locale"
import { assert, assertUnreachable } from "@/lib/utils/assertions"
import { freeze } from "@/lib/utils/collections"
import {
  addDays,
  browserTimeZone,
  getDayObject,
  getEpochMillis,
  getTimestamp,
  isCurrentYear,
  minutesBetween,
  now,
  sameDay,
  today,
} from "./date-utils"
import {
  isCity,
  isContinent,
  isCountry,
  isLocation,
  isNeighborhood,
  isRegion,
  isState,
  isTerritory,
} from "./entity-utils"
import { dayRangeForTimeFrame } from "./time-frame-utils"
import { totalPeople } from "./trip-utils"

export {
  formatWeatherDay,
  formatWeatherTemp,
  formatWindSpeed,
} from "./weather-utils"

// Intl has no ordinal *formatter* yet (PluralRules only gives the ordinal
// *category*), so we map each category to a per-language suffix ourselves.
// Keyed by primary language subtag. CJK languages mark ordinals with a prefix
// (第) we don't model, so they get an empty suffix (just the number). Unknown
// languages fall back to English.
const ORDINAL_SUFFIXES_BY_LANG: Record<
  string,
  Partial<Record<Intl.LDMLPluralRule, string>>
> = {
  en: { one: "st", two: "nd", few: "rd", other: "th" }, // 1st, 2nd, 3rd, 4th
  fr: { one: "er", other: "e" }, // 1er, 2e, 3e
  es: { other: "º" }, // 1º, 2º (masculine default)
  it: { other: "º" },
  pt: { other: "º" },
  de: { other: "." }, // 1., 2., 3.
  nl: { one: "ste", other: "de" }, // 1ste, 2de
  sv: { one: ":a", two: ":a", other: ":e" }, // 1:a, 3:e
  ru: { other: "-й" }, // 1-й, 2-й
  uk: { other: "-й" },
  ja: { other: "" }, // prefix-based; no suffix
  zh: { other: "" },
  ko: { other: "" },
}

// Example: formatOrdinal("en-US", 7) -> "7th"; formatOrdinal("fr", 1) -> "1re".
export const formatOrdinal = (locale: string, n: number): string => {
  const lang = parseLocale(locale).language ?? "en"
  const suffixes = ORDINAL_SUFFIXES_BY_LANG[lang] ?? ORDINAL_SUFFIXES_BY_LANG.en
  try {
    const rule = new Intl.PluralRules(locale, { type: "ordinal" }).select(n)
    return `${n}${suffixes[rule] ?? suffixes.other ?? ""}`
  } catch {
    return `${n}`
  }
}

export function formatNumber(locale: string, number: number) {
  return new Intl.NumberFormat(locale).format(number)
}

// Star rating to one decimal place, localized separator ("4.6" / "4,6"). With
// more digits, trailing zeros past the first decimal drop ("4.9", "4.92").
export function formatRating(
  locale: string,
  rating: number,
  fractionDigits = 1
) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: fractionDigits,
  }).format(rating)
}

export function formatDistance(
  locale: string,
  distance: number,
  unit: UnitSystem
): string {
  // Intl unit style localizes the "km"/"mi" abbreviation and grouping in one
  // pass. Distances >= 100 round to whole numbers; below that we keep up to two
  // decimals. (The old >= 100 branch used a bare toLocaleString() that ignored
  // the locale, so this also makes both branches consistent.)
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: unit === "metric" ? "kilometer" : "mile",
    unitDisplay: "short",
    maximumFractionDigits: distance >= 100 ? 0 : 2,
  }).format(distance)
}

const SQ_FT_PER_SQ_M = 10.7639

// Room/area size given in square feet (imperial source). Converts to m² for
// metric users. Intl unit style doesn't sanction square-meter/square-foot, so
// we localize the number (grouping) and append the (locale-invariant) symbol.
export function formatArea(
  locale: string,
  sqFt: number,
  unit: UnitSystem
): string {
  const value = unit === "metric" ? sqFt / SQ_FT_PER_SQ_M : sqFt
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(value)
  return unit === "metric" ? `${formatted} m²` : `${formatted} sq ft`
}

// Compact magnitude (1.2K / 12.3M) via Intl compact short. Replaces the
// hand-rolled statUnits ladder; no longer emits a "Q" past trillion.
export function formatStat(locale: string, number: number) {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(number)
}

function currencyFormat(
  locale: string,
  opts: Intl.NumberFormatOptions
): Pick<Intl.NumberFormat, "format"> {
  try {
    return new Intl.NumberFormat(locale, opts)
  } catch (error) {
    if (!(error instanceof RangeError)) throw error
    const plain = new Intl.NumberFormat(locale, {
      minimumFractionDigits: opts.minimumFractionDigits,
      maximumFractionDigits: opts.maximumFractionDigits,
    })
    return { format: (n: number) => `${plain.format(n)} ${opts.currency}` }
  }
}

// Example: "$123.45" or "¥123" depending on currency.
// precise=true defers to the currency's ISO 4217 natural decimals (Intl.NumberFormat default).
// precise=false forces 0 decimals for compact display.
export const formatMoney = (
  locale: string,
  money: number,
  precise = true,
  currency = "USD"
) => {
  const opts: Intl.NumberFormatOptions = {
    ...MONEY_DISPLAY_OPTIONS,
    currency,
  }
  if (!precise) {
    opts.maximumFractionDigits = 0
    opts.minimumFractionDigits = 0
  }
  return currencyFormat(locale, opts).format(money)
}

export const formatPriceRange = (
  locale: string,
  min: number | undefined,
  max: number | undefined,
  currency = "USD"
) => {
  if (min == null && max == null) return ""

  if (min != null && max != null && min !== max) {
    return `${formatMoney(locale, min, false, currency)}-${formatMoney(locale, max, false, currency)}`
  }

  return formatMoney(locale, min ?? max ?? 0, false, currency)
}

// Rounds up to the nearest whole unit before formatting, matching airline UI behavior.
// Airlines ceil prices (e.g. $159.40 -> $160), so we do the same.
export const formatFlightPrice = (
  locale: string,
  money: number,
  currency = "USD"
) => {
  return formatMoney(locale, Math.ceil(money), false, currency)
}

export const formatPrice = (
  locale: string,
  price: number,
  currency = "USD"
) => {
  return currencyFormat(locale, { style: "currency", currency }).format(price)
}

export const formatPlacePrice = (locale: string, place: Place) => {
  switch (place.type) {
    case "activity":
      return formatMoney(locale, place.price, false)
    default:
      return undefined
  }
}

// One entry per ISO 4217 code the user ever sees, so this never needs evicting.
// Locale.useDollarSign() calls through here on every render of PlaceLabel,
// PlacePrice and PlaceMarker — ~300 times on a 96-card grid.
const symbolByCurrency = new Map<string, string>()

// Returns the narrow currency symbol for an ISO 4217 code (e.g. "EUR" -> "€"),
// used to render relative price tiers like "€€€". Many currencies have no
// single-glyph symbol (CZK -> "Kč", PLN -> "zł"), which repeats badly as a
// price tier, so we fall back to "$" unless the symbol is a single character.
export const currencySymbol = (currencyCode?: string): string => {
  if (!currencyCode) return "$"

  const cached = symbolByCurrency.get(currencyCode)
  if (cached) return cached

  let symbol = "$"
  try {
    const parts = new Intl.NumberFormat(
      "en-US", // locale doesn't matter here because we only care about the symbol
      {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "narrowSymbol",
      }
    ).formatToParts(0)
    const narrow = parts.find((part) => part.type === "currency")?.value
    if (narrow && [...narrow].length === 1) symbol = narrow
  } catch {
    // Keep the fallback.
  }

  symbolByCurrency.set(currencyCode, symbol)
  return symbol
}

// CLDR display name for an ISO 4217 code (e.g. "USD" -> "US Dollar" /
// "dollar des États-Unis"), matching iOS's Locale.localizedString(forCurrencyCode:).
// Falls back to `fallback` (typically the backend-provided English name) and
// then the code itself: DisplayNames throws on malformed codes and returns
// undefined (fallback: "none") for well-formed codes CLDR doesn't know.
export const formatCurrencyName = (
  locale: string,
  currencyCode: string,
  fallback?: string
): string => {
  try {
    const name = new Intl.DisplayNames([locale], {
      type: "currency",
      fallback: "none",
    }).of(currencyCode)
    return name || fallback || currencyCode
  } catch {
    return fallback || currencyCode
  }
}

export const formatRestaurantPrice = (
  restaurant: Restaurant,
  dollarSign: string
) => {
  return restaurant.price_level !== undefined && restaurant.price_level !== null
    ? dollarSign.repeat(restaurant.price_level)
    : undefined
}

// Population is census data — most geos carry an exact count — so digits are the
// honest rendering and "430 thousand" would throw away real precision. Past a
// million those digits stop informing, so Intl's own magnitude words take over
// ("1.3 million", "1,3 Millionen", "100万"). lessPrecision caps the mantissa at
// 3 significant figures and one decimal, keeping "123.5 million" and CJK's
// "134.7万" from reintroducing the same noise one magnitude up.
const COMPACT_POPULATION_FROM = 1_000_000

export const formatPopulation = (locale: string, count: number) => {
  if (count < COMPACT_POPULATION_FROM) return formatNumber(locale, count)
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "long",
    maximumFractionDigits: 1,
    maximumSignificantDigits: 3,
    roundingPriority: "lessPrecision",
  }).format(count)
}

// Example: "Aug 7"
export const formatDate = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return monthDayFormat(locale, timeZone).format(getEpochMillis(timestamp))
}

// Example: "Aug 7"
export const formatDayObjectAsMonthDay = (locale: string, day: DayObject) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return monthDayFormat(locale, zone).format(timestamp.epoch_millis)
}

// Example: "August 7"
export const formatDayObjectAsFullMonthDay = (
  locale: string,
  day: DayObject
) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return fullMonthDayFormat(locale, zone).format(timestamp.epoch_millis)
}

// Example: "Aug"
export const formatMonth = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return monthFormat(locale, timeZone).format(getEpochMillis(timestamp))
}

export const formatDay = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return dayFormat(locale, timeZone).format(getEpochMillis(timestamp))
}

// Example: "Aug"
export const formatDayObjectAsMonth = (locale: string, day: DayObject) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return monthFormat(locale, zone).format(timestamp.epoch_millis)
}

// Example: "August"
export const formatDayObjectAsFullMonth = (locale: string, day: DayObject) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return fullMonthFormat(locale, zone).format(timestamp.epoch_millis)
}

export const formatMonthIntegerAsMonth = (locale: string, month: number) => {
  return formatDayObjectAsMonth(locale, { ...ARBITRARY_DAY_OBJECT, month })
}

export const formatMonthIntegerAsFullMonth = (
  locale: string,
  month: number
) => {
  return formatDayObjectAsFullMonth(locale, { ...ARBITRARY_DAY_OBJECT, month })
}

// Example: May 2023
export const formatYearMonth = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return monthYearFormat(locale, timeZone).format(getEpochMillis(timestamp))
}

// Example: "May 30, 2023"
export const formatYearMonthDay = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return yearMonthDayFormat(locale, timeZone).format(getEpochMillis(timestamp))
}

export const formatYearMonthDayHourMinSecTz = (
  timestamp: Timestamp,
  timeZone: string
) => {
  return yearMonthDayHourMinSecTzFormat(timeZone).format(
    getEpochMillis(timestamp)
  )
}

export const formatDayObjectAsYYMMDD = (day: DayObject) => {
  return `${day.year}-${day.month.toString().padStart(2, "0")}-${day.day.toString().padStart(2, "0")}`
}

// Example: "11/23/2025"
export const formatDayObjectAsMMDDYYYY = (day: DayObject) => {
  return `${day.month.toString().padStart(2, "0")}/${day.day.toString().padStart(2, "0")}/${day.year}`
}

// Example: "August 2024"
export const formatDayObjectAsMonthYear = (locale: string, day: DayObject) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return monthYearFormat(locale, zone).format(timestamp.epoch_millis)
}

// Example: "May 30, 2023"
export const formatDayObjectAsYearMonthDay = (
  locale: string,
  day: DayObject
) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return yearMonthDayFormat(locale, zone).format(timestamp.epoch_millis)
}

// Example: "Fri, Nov 30, 2023"
export const formatDayObjectAsWeekdayMonthDayYear = (
  locale: string,
  day: DayObject
) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return `${weekdayFormat(locale, zone).format(
    timestamp.epoch_millis
  )}, ${yearMonthDayFormat(locale, zone).format(timestamp.epoch_millis)}`
}

const formatTimeWithNarrowSpace = (timeString: string) => {
  return timeString.replace(/(\d)\s*(AM|PM)/g, "$1\u202F$2")
}

// Intl.DateTimeFormat.formatRange takes the whitespace around a range's dash
// from the running engine's ICU, and engines disagree even on whether there is
// any: Node 24 emits thin spaces where current Chrome and Safari emit plain
// ones, and for some locales Node sets the dash tight where Safari spaces it
// (en-CA "Oct 30–Nov 4" against "Oct 30 – Nov 4"). Either way a server-rendered
// range hydrates as a text mismatch, so every range dash gets the thin spaces
// the hand-built ranges here already use. Always range through this rather than
// calling formatRange directly.
const formatRangeStable = (
  formatter: Intl.DateTimeFormat,
  startMs: number,
  endMs: number
) => {
  return formatter
    .formatRange(startMs, endMs)
    .replace(/\s*\u2013\s*/g, "\u2009\u2013\u2009")
}

// Example: "12:40 PM"
export const formatHourMinute = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return formatTimeWithNarrowSpace(
    hourMinFormat(locale, timeZone).format(getEpochMillis(timestamp))
  )
}

// Example: "12:40 PM PT"
// Example: "12:40 PM Japan Time"
export const formatHourMinuteZone = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  const epochMillis = getEpochMillis(timestamp)
  let string = hourMinZoneFormat(locale, timeZone).format(epochMillis)
  string = formatTimeWithNarrowSpace(string)
  return string.length > 25
    ? formatTimeWithNarrowSpace(
        hourMinCompactZoneFormat(locale, timeZone).format(epochMillis)
      )
    : string
}

// Formats to the given time zone as a short string, the same string
// that would appear in formatHourMinuteZone() but without the time.
//
// Example: "PT"
// Example: "Japan Time"
export const formatTimeZone = (locale: string, timeZone: string): string => {
  const parts = hourMinZoneFormat(locale, timeZone).formatToParts(
    ARBITRARY_EPOCH_MILLIS
  )
  const zonePart = parts.find((part) => part.type === "timeZoneName")
  assert(zonePart, "couldn't find time zone name among string parts")

  if (zonePart.value.length > 15) {
    const compactParts = hourMinCompactZoneFormat(
      locale,
      timeZone
    ).formatToParts(ARBITRARY_EPOCH_MILLIS)
    const compactZonePart = compactParts.find(
      (part) => part.type === "timeZoneName"
    )
    assert(
      compactZonePart,
      "couldn't find time zone name among compact string parts"
    )
    return compactZonePart.value
  } else {
    return zonePart.value
  }
}

// Example: "12:40 PM"
export const formatTimeOfDayAsHourMinute = (
  locale: string,
  timeOfDay: TimeOfDay
) => {
  // We don't care about the the day here, because we're just
  // formatting a time. And we don't need to know what time zone
  // we're working with because we were given a specific TimeOfDay.
  // But our Intl formatter needs (and ignores) both, so supply
  // arbitrary values.
  const timestamp = getTimestamp(
    ARBITRARY_DAY_OBJECT,
    timeOfDay,
    ARBITRARY_TIME_ZONE
  )
  return formatTimeWithNarrowSpace(
    formatHourMinute(locale, timestamp, ARBITRARY_TIME_ZONE)
  )
}

const formatTimeOfDayWithOptions = (
  locale: string,
  timeOfDay: TimeOfDay,
  options: Intl.DateTimeFormatOptions,
  includeDayPeriod = true
) => {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: ARBITRARY_TIME_ZONE,
    ...options,
  })
  const ms = getEpochMillis(
    getTimestamp(ARBITRARY_DAY_OBJECT, timeOfDay, ARBITRARY_TIME_ZONE)
  )
  return formatter
    .formatToParts(ms)
    .filter((part) => includeDayPeriod || part.type !== "dayPeriod")
    .map((part) => part.value)
    .join("")
    .replace(/\s+/g, " ")
    .trim()
}

const getTimeOfDayDayPeriod = (
  locale: string,
  timeOfDay: TimeOfDay,
  options: Intl.DateTimeFormatOptions
) => {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: ARBITRARY_TIME_ZONE,
    ...options,
  })
  const ms = getEpochMillis(
    getTimestamp(ARBITRARY_DAY_OBJECT, timeOfDay, ARBITRARY_TIME_ZONE)
  )
  return formatter.formatToParts(ms).find((part) => part.type === "dayPeriod")
    ?.value
}

// Example: "5 PM" (on the hour), "12:40 PM" (with minutes)
export const formatTimeOfDayAsCompactHourMinute = (
  locale: string,
  timeOfDay: TimeOfDay
) => {
  const options =
    timeOfDay.minute === 0 ? HOUR_DISPLAY_OPTIONS : HOUR_MIN_DISPLAY_OPTIONS
  return formatTimeWithNarrowSpace(
    formatTimeOfDayWithOptions(locale, timeOfDay, options)
  )
}

// Example: "9 AM – 5 PM" (en-US), "9:00 a.m. – 5:00 p.m." (es), "13 h – 17 h" (fr)
// formatRange elides the shared meridiem per-locale; hour-only options when both
// endpoints are on the hour let Intl drop ":00" without locale-unsafe surgery.
export const formatTimeOfDayAsHourMinuteRange = (
  locale: string,
  start: TimeOfDay,
  end: TimeOfDay
) => {
  const startOnTheHour = start.minute === 0
  const endOnTheHour = end.minute === 0
  const startMs = getEpochMillis(
    getTimestamp(ARBITRARY_DAY_OBJECT, start, ARBITRARY_TIME_ZONE)
  )
  const endMs = getEpochMillis(
    getTimestamp(ARBITRARY_DAY_OBJECT, end, ARBITRARY_TIME_ZONE)
  )

  if (startOnTheHour === endOnTheHour) {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: ARBITRARY_TIME_ZONE,
      ...(startOnTheHour ? HOUR_DISPLAY_OPTIONS : HOUR_MIN_DISPLAY_OPTIONS),
    })
    // formatRange needs start < end, so overnight ranges (end <= start) format
    // each end and join; same-day endpoints keep Intl from injecting a date.
    if (endMs > startMs) {
      return formatTimeWithNarrowSpace(
        formatRangeStable(formatter, startMs, endMs)
      )
    }
    return formatTimeWithNarrowSpace(
      `${formatter.format(startMs)} – ${formatter.format(endMs)}`
    )
  }

  const startOptions = startOnTheHour
    ? HOUR_DISPLAY_OPTIONS
    : HOUR_MIN_DISPLAY_OPTIONS
  const endOptions = endOnTheHour
    ? HOUR_DISPLAY_OPTIONS
    : HOUR_MIN_DISPLAY_OPTIONS
  const startDayPeriod = getTimeOfDayDayPeriod(locale, start, startOptions)
  const endDayPeriod = getTimeOfDayDayPeriod(locale, end, endOptions)
  const elideStartDayPeriod =
    startDayPeriod !== undefined &&
    endDayPeriod !== undefined &&
    startDayPeriod === endDayPeriod
  const startStr = formatTimeOfDayWithOptions(
    locale,
    start,
    startOptions,
    !elideStartDayPeriod
  )
  const endStr = formatTimeOfDayWithOptions(locale, end, endOptions)

  return formatTimeWithNarrowSpace(`${startStr} – ${endStr}`)
}

export const formatTimeOfDayAs24HourMinute = (
  locale: string,
  timeOfDay: TimeOfDay
) => {
  // We don't care about the the day here, because we're just
  // formatting a time. And we don't need to know what time zone
  // we're working with because we were given a specific TimeOfDay.
  // But our Intl formatter needs (and ignores) both, so supply
  // arbitrary values.
  const timestamp = getTimestamp(
    ARBITRARY_DAY_OBJECT,
    timeOfDay,
    ARBITRARY_TIME_ZONE
  )
  return formatTimeWithNarrowSpace(
    hourMin24Format(locale, ARBITRARY_TIME_ZONE).format(
      getEpochMillis(timestamp)
    )
  )
}

// Example: "August 7 at 12:41 PM"
export const formatDateHourMinute = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return formatTimeWithNarrowSpace(
    monthDayHourMinFormat(locale, timeZone).format(getEpochMillis(timestamp))
  )
}

// Example: "M" - narrow (single-character) weekday abbrev in the user's locale
export const formatWeekdayAbbrev = (locale: string, weekday: Date): string => {
  return new Intl.DateTimeFormat(locale, {
    weekday: "narrow",
  }).format(weekday)
}

// Example: "Mon" / "lun." / "Mo" / "月" — short (not narrow) weekday name. Use
// over formatWeekdayAbbrev where a single letter would be ambiguous.
export const formatWeekday = (locale: string, weekday: Date): string => {
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
  }).format(weekday)
}

// Example: "Monday" / "lundi" / "Montag" / "月曜日" — full weekday name.
export const formatFullWeekday = (locale: string, weekday: Date): string => {
  return new Intl.DateTimeFormat(locale, FULL_WEEKDAY_FORMAT_OPTIONS).format(
    weekday
  )
}

// Example: "Mon, Aug 7"
export const formatWeekdayMonthDay = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return weekdayMonthDayFormat(locale, timeZone).format(
    getEpochMillis(timestamp)
  )
}

// Example: "Mon, August 7"
export const formatWeekdayFullMonthDay = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  return weekdayFullMonthDayFormat(locale, timeZone).format(
    getEpochMillis(timestamp)
  )
}

// Example: "Mon" — short weekday name (no month/day) for a calendar day.
export const formatDayObjectAsWeekday = (locale: string, day: DayObject) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return weekdayFormat(locale, zone).format(getEpochMillis(timestamp))
}

// Example: "Mon, Aug 7"
export const formatDayObjectAsWeekdayMonthDay = (
  locale: string,
  day: DayObject
) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return weekdayMonthDayFormat(locale, zone).format(getEpochMillis(timestamp))
}

// Example: "Monday, August 7"
export const formatDayObjectAsFullWeekdayFullMonthDay = (
  locale: string,
  day: DayObject
) => {
  const zone = ARBITRARY_TIME_ZONE
  const timestamp = getTimestamp(day, ARBITRARY_TIME_OF_DAY, zone)
  return fullWeekdayFullMonthDayFormat(locale, zone).format(
    getEpochMillis(timestamp)
  )
}

export const formatWeekdayMonthDayZone = (
  locale: string,
  day: DayObject,
  timeZone: string
) => {
  return weekdayMonthDayZoneFormat(locale, timeZone).format(
    getTimestamp(day, ARBITRARY_TIME_OF_DAY, timeZone).epoch_millis
  )
}

// Example: "Mon 12p"
export const formatWeekdayHour = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  const epoch = getEpochMillis(timestamp)
  return `${weekdayFormat(locale, timeZone).format(epoch)} ${hourFormat(
    locale,
    timeZone
  )
    .format(epoch)
    .replace(/ /g, "")
    .replace("AM", "a")
    .replace("PM", "p")}`
}

// Example: Apr 8
// Example: Apr 8
// Example: Apr 8-10
// Example: Apr 8–May 31
// Example: Apr 8, 2023–Mar 16, 2024
export const formatTimestampsAsRange = (
  locale: string,
  startDate: Timestamp,
  endDate: Timestamp | undefined,
  timeZone: string
) => {
  if (startDate && endDate) {
    const startEpoch = getEpochMillis(startDate)
    const endEpoch = getEpochMillis(endDate)
    if (startEpoch > endEpoch) {
      throw new RangeError("Start date must be before end date")
    }

    return formatRangeStable(
      monthDayFormat(locale, timeZone),
      startEpoch,
      endEpoch
    )
  } else {
    return formatDate(locale, startDate, timeZone)
  }
}

// Example: Apr 8
// Example: Apr 8
// Example: Apr 8-10
// Example: Apr 8–May 31
// Example: Apr 8, 2023–Mar 16, 2024
export const formatDayObjectsAsRange = (
  locale: string,
  startDay: DayObject,
  endDay: DayObject | undefined,
  includeYear = false
) => {
  const zone = ARBITRARY_TIME_ZONE
  const startTime = getTimestamp(startDay, ARBITRARY_TIME_OF_DAY, zone)
  const endTime = endDay && getTimestamp(endDay, ARBITRARY_TIME_OF_DAY, zone)

  if (endTime) {
    const startEpoch = getEpochMillis(startTime)
    const endEpoch = getEpochMillis(endTime)
    if (startEpoch > endEpoch) {
      throw new RangeError("Start date must be before end date")
    }

    if (includeYear) {
      if (sameDay(startDay, endDay)) {
        return monthDayYearFormat(locale, zone).format(startEpoch)
      } else if (startDay.year === endDay.year) {
        // Same year, only show year at the end
        return `${monthDayFormat(locale, zone).format(startEpoch)}\u2009–\u2009${monthDayYearFormat(locale, zone).format(endEpoch)}`
      } else {
        // Different years, show both years
        return `${monthDayYearFormat(locale, zone).format(startEpoch)}\u2009–\u2009${monthDayYearFormat(locale, zone).format(endEpoch)}`
      }
    } else if (sameDay(startDay, endDay)) {
      // Same day, just return the formatted date without year
      return monthDayFormat(locale, zone).format(startEpoch)
    } else if (startDay.year === endDay.year) {
      // Same year, use the browser's formatRange for smart formatting (e.g., "Mar 1 – 5")
      return formatRangeStable(
        monthDayFormat(locale, zone),
        startEpoch,
        endEpoch
      )
    } else {
      // Different years, but don't show years - format both dates manually
      return `${monthDayFormat(locale, zone).format(startEpoch)}\u2009–\u2009${monthDayFormat(locale, zone).format(endEpoch)}`
    }
  } else {
    return includeYear
      ? monthDayYearFormat(locale, zone).format(getEpochMillis(startTime))
      : monthDayFormat(locale, zone).format(getEpochMillis(startTime))
  }
}

// Example: Apr 8
// Example: Apr 8
// Example: Apr 8-10
// Example: Apr 8–May 31
// Example: Apr 8, 2023–Mar 16, 2024
export const formatDayRange = (
  locale: string,
  range: DayRange,
  includeYear = false
) => {
  return (
    range.from &&
    formatDayObjectsAsRange(locale, range.from, range.to, includeYear)
  )
}

// Example: "59m"
// Example: "12h 32m"
// Composed from Intl.NumberFormat unit-style parts (not Intl.DurationFormat,
// which is too new for the browserslist and unpolyfilled).
export function formatMinutesAsHoursMinutes(locale: string, minutes: number) {
  const hours = Math.floor(minutes / 60)
  const minuteRemainder = minutes % 60
  const h = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "hour",
    unitDisplay: "narrow",
  }).format(hours)
  const m = new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "minute",
    unitDisplay: "narrow",
  }).format(minuteRemainder)

  if (hours === 0) {
    return m
  }
  if (minuteRemainder === 0) {
    return h
  }
  return `${h} ${m}`
}

export function formatMinutesAsHours(locale: string, minutes: number) {
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "hour",
    unitDisplay: "narrow",
  }).format(Math.floor(minutes / 60))
}

// Example: ""
// Example: "+1d"
// Example: "-1d"
export function formatDayBoundarySuffix(locale: string, dayDifference: number) {
  if (dayDifference === 0) {
    return ""
  }
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "day",
    unitDisplay: "narrow",
    signDisplay: "exceptZero",
  }).format(dayDifference)
}

// Localized day-count label: "6 days" / "6 jours" / "6 Tage". Replaces
// pluralize("day", n), which only ever produces English plurals.
export function formatDayCount(locale: string, days: number): string {
  return new Intl.NumberFormat(locale, {
    style: "unit",
    unit: "day",
    unitDisplay: "long",
  }).format(days)
}

// i18n: whole-template keys so each locale owns its word order (cf. getPlaceTitle)
export const formatTimeFrame = (
  locale: string,
  t: Translator,
  timeFrame: TimeFrame,
  options: { includeYear?: boolean } = {}
) => {
  const monthString =
    timeFrame.months && formatTimeFrameMonths(locale, timeFrame.months)
  if (timeFrame.start_date && timeFrame.duration) {
    const dayRange = dayRangeForTimeFrame(timeFrame)
    return formatDayRange(locale, dayRange, options.includeYear)
  } else if (timeFrame.start_date) {
    const formattedStartDate = options.includeYear
      ? formatDayObjectAsYearMonthDay(locale, timeFrame.start_date)
      : formatDayObjectAsMonthDay(locale, timeFrame.start_date)
    return t("timeFrameStarting", { date: formattedStartDate })
  } else if (timeFrame.duration && monthString) {
    return t("timeFrameDurationInMonths", {
      duration: formatDayCount(locale, timeFrame.duration),
      months: monthString,
    })
  } else if (timeFrame.duration) {
    return formatDayCount(locale, timeFrame.duration)
  } else {
    return monthString
  }
}

export const formatTimeFrameMonths = (
  locale: string,
  months: number[],
  format: "compact" | "full" = "compact",
  today: DayObject = { ...ARBITRARY_DAY_OBJECT, month: 1 }
) => {
  if (today) {
    const cutoff = months.findIndex((m) => m >= today.month)
    if (cutoff > -1) {
      const firstMonths = months.slice(0, cutoff)
      const lastMonths = months.slice(cutoff)
      months = [...lastMonths, ...firstMonths]
    }
  }
  return months
    ?.map((month) =>
      format === "compact"
        ? formatDayObjectAsMonth(locale, {
            ...ARBITRARY_DAY_OBJECT,
            month,
          })
        : formatDayObjectAsFullMonth(locale, {
            ...ARBITRARY_DAY_OBJECT,
            month,
          })
    )
    .join(", ")
}

export function formatTripVibe(vibe: TripVibe) {
  const title = vibe.title.replace(" and ", " & ")
  return vibe.emoji ? `${vibe.emoji} ${title}` : title
}

export function formatTravelerCount(
  travelers: TravelerGroup,
  zeroLabel = "Who"
): string {
  const count = totalPeople(travelers)
  return count === 0 ? zeroLabel : `${count} ${pluralize("traveler", count)}`
}

export function formatTravelerSummary(travelers: TravelerGroup) {
  const adults = travelers.adult_count
  const children = travelers.child_count
  const infants = travelers.infant_count

  const res: string[] = []
  if (adults > 0) {
    res.push(`${adults} ${pluralize("adult", adults)}`)
  }
  if (children > 0) {
    res.push(`${children} ${pluralize("child", children)}`)
  }
  if (infants > 0) {
    res.push(`${infants} ${pluralize("infant", infants)}`)
  }
  return res.join(", ")
}

export function formatFlightTravelerSummary(travelers: FlightTravelers) {
  const adults = travelers.adult_count
  const children = travelers.child_count
  const infantsOnLap = travelers.infant_on_lap_count
  const infantsInSeat = travelers.infant_in_seat_count

  const res: string[] = []
  if (adults > 0) {
    res.push(`${adults} ${pluralize("adult", adults)}`)
  }
  if (children > 0) {
    res.push(`${children} ${pluralize("child", children)}`)
  }
  const totalInfants = infantsOnLap + infantsInSeat
  if (totalInfants > 0) {
    res.push(`${totalInfants} ${pluralize("infant", totalInfants)}`)
  }
  return res.join(", ")
}

export function formatBudget(budget: Budget): string {
  switch (budget) {
    case "any":
      return "Any budget"
    case "$":
      return "On a budget"
    case "$$":
      return "Sensibly priced"
    case "$$$":
      return "Upscale"
    case "$$$$":
      return "Luxury"
    default:
      return assertUnreachable(budget)
  }
}

export function formatPlaceType(
  type: PlaceType,
  plural = true,
  capitalize = false
): string {
  let formatted = ""
  switch (type) {
    case "hotel":
      formatted = "stay"
      break
    case "restaurant":
    case "event":
    case "attraction":
    case "activity":
    case "location":
    case "airport":
      formatted = type
      break
    default:
      assertUnreachable(type)
  }

  formatted = plural ? pluralize(formatted) : formatted
  return capitalize
    ? formatted.charAt(0).toUpperCase() + formatted.slice(1)
    : formatted
}

export function formatEventType(
  type: ItineraryEventType,
  plural = true
): string {
  switch (type) {
    case "flight":
      return plural ? "flights" : "flight"
    case "hotel":
      return plural ? "stays" : "stay"
    case "restaurant":
      return plural ? "restaurants" : "restaurant"
    case "event":
      return plural ? "events" : "event"
    case "attraction":
      return plural ? "attractions" : "attraction"
    case "activity":
      return plural ? "activities" : "activity"
    case "location":
      return plural ? "locations" : "location"
    case "airport":
      return plural ? "airports" : "airport"
    case "car_rental":
      return plural ? "rental cars" : "rental car"
    default:
      return type
  }
}

export function formatPlaceLocale(place: Place) {
  const addressParts: string[] = []
  if (isLocation(place)) {
    if (isTerritory(place) || isContinent(place)) {
      return place.category.charAt(0).toUpperCase() + place.category.slice(1)
    }

    if (isCountry(place)) {
      return place.continent ? `Country, ${place.continent}` : "Country"
    }

    if (place.city && !isCity(place)) {
      addressParts.push(place.city)
    }

    if (place.state && !isState(place)) {
      addressParts.push(place.state)
    }

    if (
      place.region &&
      place.region !== place.state &&
      !isRegion(place) &&
      !isNeighborhood(place)
    ) {
      addressParts.push(place.region)
    }

    if (place.country && !isCountry(place)) {
      addressParts.push(place.country)
    }
    return [...new Set(addressParts.filter((p) => p !== place.name))].join(", ")
  } else {
    if (place.city) {
      addressParts.push(place.city)
    }
    const stateOrCountry = place.state || place.country
    if (stateOrCountry) {
      addressParts.push(stateOrCountry)
    }
    return [...new Set(addressParts)].join(", ")
  }
}

/** Short location label: drop a trailing country when a state is already shown. */
export function formatShortLocationLocale(place: Place) {
  if (!isLocation(place)) {
    return place.category_label
  }

  const { category_label: label, country, state, name } = place
  if (country && state && state !== name && label.endsWith(`, ${country}`)) {
    const withoutCountry = label.slice(0, -`, ${country}`.length)
    if (withoutCountry.includes(state)) {
      return withoutCountry
    }
  }

  return label
}

// SEO title variants, keyed by entity_ref
function placeTitleVariant(place: Place): 0 | 1 | 2 {
  // first character of the encoded entity id
  const firstChar = place?.entity_ref?.charAt(3)?.toUpperCase()

  if ("567NOPQR".includes(firstChar)) return 1
  if ("89STUVWXYZ".includes(firstChar)) return 2
  return 0
}

// formatPlaceLocale doubles as a card subtitle; for a country/territory/
// continent it returns a category label, so separate it from page titles
function placeTitleLocation(place: Place): string {
  if (isCountry(place) || isTerritory(place) || isContinent(place)) return ""
  return formatPlaceLocale(place)
}

// i18n: whole-template so each locale owns its word order and preposition
function placeTitleHead(
  t: Translator,
  key: TranslationKey,
  name: string,
  location: string
): string {
  if (!location) return name
  return t(key, { name, location })
}

// i18n: translated title tag
export function getPlaceTitle(t: Translator, place: Place): string {
  const variant = placeTitleVariant(place)

  // Build the title based on the type of the place and the variant
  let placeName = place.name
  let placeLocation = placeTitleLocation(place)
  let placeSuffix = t("placeTitleSuffixDefault")
  let headKey: TranslationKey = "placeTitleNameInLocation"

  switch (place.type) {
    case "restaurant": {
      const cuisineName = place.cuisines?.[0]?.replaceAll("_", " ")
      const cuisine = cuisineName ? ` | ${cuisineName} ` : ""
      switch (variant) {
        case 1:
          placeName = `${place.name} ${cuisine}`
          placeSuffix = t("placeTitleAskAnything")
          break
        case 2:
          placeSuffix = t("placeTitleIsItGood")
          break
        default:
          // variant 0 — control arm, keeps the default suffix
          break
      }
      break
    }
    case "location":
      switch (variant) {
        case 1:
          headKey = "placeTitleThingsToDoIn"
          // the place itself leads the location: "Paris, Île-de-France, France"
          placeLocation = [place.name, placeLocation].filter(Boolean).join(", ")
          placeSuffix = t("placeTitleExploreWithAi")
          break
        case 2:
          placeName = ""
          placeSuffix = t("placeTitleWhatToKnow")
          break
        default:
          break
      }
      break
    case "hotel":
      switch (variant) {
        case 1:
          placeSuffix = t("placeTitleQuestionsAnswered")
          break
        case 2:
          placeSuffix = t("placeTitleShouldIStayHere")
          break
        default:
          break
      }
      break
    case "attraction":
      switch (variant) {
        case 1:
          placeSuffix = t("placeTitleAskAnything")
          break
        case 2:
          placeSuffix = t("placeTitleWhatToKnow")
          break
        default:
          break
      }
      break
    default:
      break
  }

  const newPlaceName = placeName === "" ? place.name : placeName
  const head = placeTitleHead(t, headKey, newPlaceName, placeLocation)
  let title = `${head} ${placeSuffix}`
  // replace double spaces with single spaces
  title = title.replace(/\s{2,}/g, " ")
  // Google truncates around 60 chars: try to shorten the title
  if (title.length > 60) {
    const city = placeLocation.split(", ")[0] ?? ""
    title = `${placeTitleHead(t, headKey, newPlaceName, city)} ${placeSuffix}`
    if (title.length > 60) {
      title = `${newPlaceName} ${placeSuffix}`
      if (title.length > 60) {
        title = `${newPlaceName}`
      }
    }
  }

  title = title.replace(/\s{2,}/g, " ")

  return title
}

export function formatCityState(city?: string, state?: string) {
  if (city && state) {
    return `${city}, ${state}`
  }
  return city || state || ""
}

export function getGuideTitle(guide: DetailedInspiration) {
  // filter(Boolean) is like compact on Rails.
  const userName = [guide.owner.first_name, guide.owner.last_name]
    .filter(Boolean)
    .join(" ")
  return [guide.title, userName].filter(Boolean).join(" | ")
}

// Compact magnitude with a lowercase unit (e.g. "999", "1.5k", "12.3k"). Intl
// offers no option to force lowercase, so we lowercase the formatted string:
// correct for en/SI ("K" -> "k") and harmless for CJK ("万"), at the cost of
// lowercasing capitalized unit abbreviations that only appear in the millions
// for some European locales ("Mio." -> "mio."). That edge is rare for review
// counts and moot while the locale is pinned to en-US; revisit when real
// locales are wired up.
export function formatReviewCount(locale: string, count: number) {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  })
    .format(count)
    .toLowerCase()
}

export function formatEditorialRating(
  rating: EditorialRating,
  includePrefix = false
) {
  const prefix = includePrefix ? "Michelin " : ""

  switch (rating) {
    case "michelin_1_star":
      return `${prefix}One Star`
    case "michelin_2_star":
      return `${prefix}Two Stars`
    case "michelin_3_star":
      return `${prefix}Three Stars`
    case "michelin_bib":
      return `${prefix}Bib Gourmand`
    case "michelin_green_star":
      return `${prefix}Green Star`
    case "michelin_select":
      return `${prefix}Select`
    case "michelin_1_key":
      return `${prefix}One Key`
    case "michelin_2_key":
      return `${prefix}Two Keys`
    case "michelin_3_key":
      return `${prefix}Three Keys`
    default:
      return rating
  }
}

export function getRatingLabel(rating: number): string {
  if (rating < 1) return "Terrible"
  if (rating < 2) return "Poor"
  if (rating < 3) return "Average"
  if (rating < 4) return "Very good"
  return "Excellent" // for 4+
}

// Example: "59m"
// Example: "12h 32m"
export function formatActivityDuration(locale: string, minutes: number) {
  return formatMinutesAsHoursMinutes(locale, minutes)
}

// Example: "59m"
// Example: "12h 32m"
export function formatFlightDuration(locale: string, minutes: number) {
  return formatMinutesAsHoursMinutes(locale, minutes)
}

// Example: "now"
// Example: "4m ago"
// Example: "1h 59m ago"
// Example: "Apr 7, 2023"   (if more than several hours ago)
export function formatElapsedTimeOrDate(
  locale: string,
  timestamp: Timestamp,
  timeZone?: string,
  currentTime?: Timestamp,
  omitCurrentYear = false
) {
  timeZone ||= browserTimeZone()
  currentTime ||= now()

  const minutes = minutesBetween(timestamp, currentTime)
  if (minutes < 2) {
    return "Just now"
  } else if (minutes < 3 * 60) {
    return `${formatMinutesAsHoursMinutes(locale, minutes)} ago`
  } else if (
    sameDay(
      getDayObject(timestamp, timeZone),
      getDayObject(currentTime, timeZone)
    )
  ) {
    return `${formatMinutesAsHours(locale, minutes)} ago`
  } else if (omitCurrentYear && isCurrentYear(timestamp)) {
    return formatDate(locale, timestamp, timeZone)
  } else {
    return formatYearMonthDay(locale, timestamp, timeZone)
  }
}

// Short elapsed-ago format used across dashboard rows / pill bubbles.
// "today" / "12h ago" / "15d ago". Same-day shows hours; day-level otherwise.
export function formatDaysAgo(days: number | null): string {
  if (days == null) return ""
  if (days === 0) return "today"
  return `${days}d ago`
}

function spelledAgo(
  n: number,
  unit: "minute" | "hour" | "day",
  omitAgo = false
): string {
  const base = `${n} ${unit}${n === 1 ? "" : "s"}`
  return omitAgo ? base : `${base} ago`
}

// "Just now" / "5m ago" / "12h ago" / "15d ago". Sub-hour shows minutes;
// same-day shows hours; day-level otherwise. Pass spellOut for the long form:
// "Just now" / "5 minutes ago" / "12 hours ago" / "15 days ago". Pass omitAgo
// for the compact stamp without "ago" ("5m", "12h", "15d").
export function formatElapsedAgo(
  timestamp: Timestamp,
  spellOut = false,
  omitAgo = false
): string {
  const minutesElapsed = Math.floor(
    (nowMillis() - timestamp.epoch_millis) / (60 * 1000)
  )
  if (minutesElapsed < 1) return "Just now"
  if (minutesElapsed < 60) {
    return spellOut
      ? spelledAgo(minutesElapsed, "minute", omitAgo)
      : omitAgo
        ? `${minutesElapsed}m`
        : `${minutesElapsed}m ago`
  }
  const hoursElapsed = Math.floor(minutesElapsed / 60)
  if (hoursElapsed < 24) {
    return spellOut
      ? spelledAgo(hoursElapsed, "hour", omitAgo)
      : omitAgo
        ? `${hoursElapsed}h`
        : `${hoursElapsed}h ago`
  }
  const days = Math.floor(hoursElapsed / 24)
  if (spellOut) return spelledAgo(days, "day", omitAgo)
  return omitAgo ? `${days}d` : formatDaysAgo(days)
}

// Variant for ISO timestamps that supports sub-day resolution.
// Returns "Just now" / "Xm ago" when < 1 hour elapsed, "Xh ago" within the
// same day, "Xd ago" beyond that. Empty string for null/invalid input. Pass
// spellOut for the long form ("15 days ago").
export function formatIsoElapsedAgo(
  iso: string | null | undefined,
  spellOut = false
): string {
  if (!iso) return ""
  const ts = convertISODateToTimestamp(iso).epoch_millis
  if (!Number.isFinite(ts)) return ""
  return formatElapsedAgo({ epoch_millis: ts }, spellOut)
}

// Localized relative time supporting BOTH past and future, e.g. "in 2 days",
// "3 hours ago". Uses Intl.RelativeTimeFormat (numeric:"auto" yields idiomatic
// "tomorrow"/"yesterday"/"now" where the locale provides them) and picks the
// largest whole unit (day > hour > minute). Sub-minute differences read as "now".
// Example: formatRelativeTime("fr", t+2d, t) -> "dans 2 jours"
export function formatRelativeTime(
  locale: string,
  targetMillis: number,
  currentMillis: number
): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
  const diffMs = targetMillis - currentMillis
  if (Math.abs(diffMs) < 60_000) {
    return rtf.format(0, "second")
  }
  const days = Math.trunc(diffMs / 86_400_000)
  if (Math.abs(days) >= 1) return rtf.format(days, "day")
  const hours = Math.trunc(diffMs / 3_600_000)
  if (Math.abs(hours) >= 1) return rtf.format(hours, "hour")
  return rtf.format(Math.trunc(diffMs / 60_000), "minute")
}

const fullWeekdayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...FULL_WEEKDAY_FORMAT_OPTIONS,
  })
}

export function formatMessageDate(
  locale: string,
  timestamp: Timestamp,
  timeZone: string
): string {
  const messageDay = getDayObject(timestamp, timeZone)
  const todayDay = today(timeZone)
  const yesterdayDay = addDays(todayDay, -1)

  if (sameDay(messageDay, todayDay)) {
    return "Today"
  } else if (sameDay(messageDay, yesterdayDay)) {
    return "Yesterday"
  } else {
    const weekday = fullWeekdayFormat(locale, timeZone).format(
      getEpochMillis(timestamp)
    )
    const month = fullMonthFormat(locale, timeZone).format(
      getEpochMillis(timestamp)
    )
    // formatOrdinal returns the number WITH its suffix (e.g. "7th").
    const day = formatOrdinal(locale, messageDay.day)
    return `${weekday}, ${month} ${day}`
  }
}

export function formatList(locale: string, values: string[]): string {
  const lf = new Intl.ListFormat(locale)
  return lf.format(values)
}

export function elide(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 1) + "…"
}

// remove site suffix after pipe in html page titles
export function cleanPageTitle(title: string): string {
  return title.split("|")[0].trim() || title.trim()
}

// url-safe slug of a page title, e.g. for filenames and query params
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
}

export function formatTravelerGroup({
  adult_count,
  child_count,
  infant_count,
}: TravelerGroup) {
  const res: string[] = []
  if (adult_count > 0) {
    res.push(`${adult_count} ${pluralize("adult", adult_count)}`)
  }
  if (child_count > 0) {
    res.push(`${child_count} ${pluralize("child", child_count)}`)
  }
  if (infant_count > 0) {
    res.push(`${infant_count} ${pluralize("infant", infant_count)}`)
  }
  return res.join(", ")
}

// Given a list of values, returns an expanded list of values
// with "and" and commas inserted to make a serial list clause.
//
// Examples (en-US, Oxford comma):
// ["a", "b"] => ["a", " and ", "b"]
// ["a", "b", "c"] => ["a", ", ", "b", ", and ", "c"]
export function formatSerialListClause(
  locale: string,
  values: Array<string | JSX.Element>
) {
  if (values.length <= 1) return values
  // Intl.ListFormat only handles strings, so we feed it each value's index as a
  // placeholder ("0", "1", …) and let it interleave the locale-correct
  // connectors. Each "element" part comes back with that index as its value, so
  // we swap it for the original entry — which may be a JSX node, not a string.
  const indices = values.map((_, index) => String(index))
  const parts = new Intl.ListFormat(locale, {
    type: "conjunction",
  }).formatToParts(indices)
  return parts.map((part) =>
    part.type === "element" ? values[Number(part.value)] : part.value
  )
}

// Example:
// "palo_alto" => "Palo Alto"
export function formatSnakeCase(text: string | undefined): string {
  if (!text) {
    return ""
  }
  // Split by underscore and capitalize each word
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

// Example:
// "ITINERARY" => "Itinerary"
// "USER_INTENT" => "User Intent"
export function formatToNormalCase(text: string | undefined): string {
  if (!text) return ""
  return text
    .split(/[\s_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

// Use this internally in format utils if needed for working with
// APIs that need a time zone, but only if the zone from our
// perspective is truly unimportant. We should be able to change
// this constant at any time -- from UTC to America/Los_Angeles,
// for example -- without breaking anything.
const ARBITRARY_TIME_ZONE = "UTC"
const ARBITRARY_DAY_OBJECT: DayObject = { year: 2023, month: 1, day: 1 }
const ARBITRARY_TIME_OF_DAY: TimeOfDay = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
}
const ARBITRARY_EPOCH_MILLIS = 0

const FULL_WEEKDAY_FORMAT_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  weekday: "long",
})

const MONEY_DISPLAY_OPTIONS = freeze<Intl.NumberFormatOptions>({
  style: "currency",
  currency: "USD",
})

const DATE_MONTH_DAY_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "short",
  day: "numeric",
})

const DATE_FULL_MONTH_DAY_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "long",
  day: "numeric",
})

const DATE_DAY_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  day: "numeric",
})

const DATE_MONTH_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "short",
})

const DATE_FULL_MONTH_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "long",
})

const DATE_MONTH_YEAR_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "long",
  year: "numeric",
})

const DATE_SHORT_MONTH_YEAR_DISPLAY_OPTIONS =
  freeze<Intl.DateTimeFormatOptions>({
    month: "short",
    year: "numeric",
  })

const DATE_MONTH_DAY_YEAR_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  month: "short",
  day: "numeric",
  year: "numeric",
})

const DATE_YEAR_MONTH_DAY_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  year: "numeric",
  month: "short",
  day: "numeric",
})

const HOUR_MIN_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  hour: "numeric",
  minute: "numeric",
  // No hour12 — Intl picks the locale's CLDR cycle (en-US 12h, en-GB/fr/de/ja 24h).
})

// Hour only — lets range formatting drop ":00" on on-the-hour endpoints.
const HOUR_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  hour: "numeric",
})

const HOUR_MIN_24_DISPLAY_OPTIONS = freeze<Intl.DateTimeFormatOptions>({
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23", // Want 24:00 to be 00:00
})

const DATE_MONTH_DAY_HOUR_MIN_DISPLAY_OPTIONS =
  freeze<Intl.DateTimeFormatOptions>({
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    // No hour12 — see HOUR_MIN_DISPLAY_OPTIONS.
  })

const DATE_YEAR_MONTH_DAY_HOUR_MIN_SEC_TZ_DISPLAY_OPTIONS =
  freeze<Intl.DateTimeFormatOptions>({
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZoneName: "short",
  })

const DATE_WEEKDAY_MONTH_DAY = freeze<Intl.DateTimeFormatOptions>({
  weekday: "short",
  month: "short",
  day: "numeric",
})

const DATE_WEEKDAY_FULL_MONTH_DAY = freeze<Intl.DateTimeFormatOptions>({
  weekday: "short",
  month: "long",
  day: "numeric",
})

const DATE_FULL_WEEKDAY_FULL_MONTH_DAY = freeze<Intl.DateTimeFormatOptions>({
  weekday: "long",
  month: "long",
  day: "numeric",
})

const DATE_WEEKDAY_MONTH_DAY_ZONE = freeze<Intl.DateTimeFormatOptions>({
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZoneName: "long",
})

const DATE_WEEKDAY = freeze<Intl.DateTimeFormatOptions>({
  weekday: "short",
})

const DATE_HOUR = freeze<Intl.DateTimeFormatOptions>({
  hour: "numeric",
  // No hour12 — see HOUR_MIN_DISPLAY_OPTIONS.
})

const yearMonthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_YEAR_MONTH_DAY_DISPLAY_OPTIONS,
  })
}
const yearMonthDayHourMinSecTzFormat = (timeZone: string) => {
  // Intentionally pinned to en-US — this feeds Schema.org JSON-LD
  // (startDate/endDate/dateModified/validFrom) and must stay locale-independent
  // for SEO. Do NOT thread `locale` here.
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    ...DATE_YEAR_MONTH_DAY_HOUR_MIN_SEC_TZ_DISPLAY_OPTIONS,
  })
}
const monthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_MONTH_DAY_DISPLAY_OPTIONS,
  })
}
const fullMonthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_FULL_MONTH_DAY_DISPLAY_OPTIONS,
  })
}
const dayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_DAY_DISPLAY_OPTIONS,
  })
}
const monthDayYearFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_MONTH_DAY_YEAR_DISPLAY_OPTIONS,
  })
}
const monthFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_MONTH_DISPLAY_OPTIONS,
  })
}
const fullMonthFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_FULL_MONTH_DISPLAY_OPTIONS,
  })
}
const monthYearFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_MONTH_YEAR_DISPLAY_OPTIONS,
  })
}
const shortMonthYearFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_SHORT_MONTH_YEAR_DISPLAY_OPTIONS,
  })
}
const hourMinFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...HOUR_MIN_DISPLAY_OPTIONS,
  })
}
const hourMin24Format = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...HOUR_MIN_24_DISPLAY_OPTIONS,
  })
}
const hourMinZoneFormat = (locale: string, timeZone: string) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "shortGeneric",
      ...HOUR_MIN_DISPLAY_OPTIONS,
    })
  } catch (e) {
    if (
      e instanceof RangeError &&
      e.message &&
      /timeZoneName/.test(e.message)
    ) {
      // older browsers don't support shortGeneric
      return new Intl.DateTimeFormat(locale, {
        timeZone,
        timeZoneName: "short",
        ...HOUR_MIN_DISPLAY_OPTIONS,
      })
    } else {
      throw e
    }
  }
}
const hourMinCompactZoneFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "short",
    ...HOUR_MIN_DISPLAY_OPTIONS,
  })
}
const monthDayHourMinFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_MONTH_DAY_HOUR_MIN_DISPLAY_OPTIONS,
  })
}
const weekdayMonthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_WEEKDAY_MONTH_DAY,
  })
}
const weekdayFullMonthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_WEEKDAY_FULL_MONTH_DAY,
  })
}
const fullWeekdayFullMonthDayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_FULL_WEEKDAY_FULL_MONTH_DAY,
  })
}
const weekdayMonthDayZoneFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_WEEKDAY_MONTH_DAY_ZONE,
  })
}
const weekdayFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_WEEKDAY,
  })
}
const hourFormat = (locale: string, timeZone: string) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...DATE_HOUR,
  })
}

// Example: "Jun 15" for current year, "Jun 2020" for past years
export const formatMonthDayOrMonthYear = (
  locale: string,
  timestamp: Timestamp,
  timeZone: string
) => {
  if (isCurrentYear(timestamp)) {
    return monthDayFormat(locale, timeZone).format(getEpochMillis(timestamp))
  } else {
    return shortMonthYearFormat(locale, timeZone).format(
      getEpochMillis(timestamp)
    )
  }
}

// Converts a star rating string (e.g., 'five_star') to a number (e.g., 5)
export function starRatingToNumber(
  star: string | undefined
): number | undefined {
  switch (star) {
    case "five_star":
      return 5
    case "four_star":
      return 4
    case "three_star":
      return 3
    case "two_star":
      return 2
    case "one_star":
      return 1
    default:
      return undefined
  }
}

// Formats a TimeOfDay object as 'HH:mm' string
export function formatTimeOfDayString(time?: {
  hour: number
  minute: number
}): string | undefined {
  if (typeof time?.hour === "number" && typeof time?.minute === "number") {
    return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`
  }
  return undefined
}
